import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms, SAFE_ZONE_VERTEX_CHUNK } from '../shaders/safeZone.glsl.js';

// RESTAURÉ le 17/10/2026 depuis public/party-mode/bundle.js (esbuild) : la
// source avait été écrasée par erreur ; les commentaires d'origine sont perdus
// (la version exacte reste dans l'historique Google Drive de ce fichier).
// Le spectre de lumière : un faisceau blanc entre dans un prisme, l'éventail
// des longueurs d'onde (Cauchy) frappe l'écran ; poussière dans les faisceaux.

const RIG = "forbidden";
export { RIG };
export const PROFILE = {
  id: "prism",
  family: "vector",
  energyLevel: 0.3,
  bpmMin: 50,
  bpmMax: 150,
  movement: 0.35,
  danceability: 0.3,
  tonality: 0.85,
  prefers: { dynamics: [0.3, 1], percussiveness: [0, 0.55] },
  enabled: true
};
const CAUCHY_A = 1.5046;
const CAUCHY_B = 42e-4;
const APEX_ANGLE = Math.PI / 3;
const LAMBDA_MIN = 380;
const LAMBDA_MAX = 700;
const DISPERSION_GAIN = 7;
const PRISM_RADIUS = 1.02;
const PRISM_Y = -2.4;
const PRISM_X_NEAR = -1.3;
const PRISM_X_FAR = -4.3;
const SCREEN_FRONT_X = 4.1;
const SCREEN_HALF_HEIGHT = 5;
const SCREEN_Y = 0;
const SCREEN_WIDTH = 6;
const CAMERA_Z = 6.2;
const THETA_BASE = 55 * Math.PI / 180;
const THETA_LFO = 6 * Math.PI / 180;
const THETA_LFO_PERIOD = 163;
const BAND_CENTER_Y = -2.75;
const AIM_ITERATIONS = 3;
const ALPHA_START = -48 * Math.PI / 180;
const ALPHA_SWEEP = 4 * Math.PI / 180;
const ALPHA_LFO_PERIOD = 97;
const TIER_WAVELENGTHS = [8, 12, 16];
const TIER_PARTICLES = [0, 110, 160];
const TIER_HALO = [false, true, true];
const MAX_BEAMS = 16 + 2;
const INCIDENT_WIDTH_PX = 14;
const SPECTRAL_WIDTH_NEAR_PX = 24;
const SPECTRAL_WIDTH_FAR_PX = 6;
const INCIDENT_LENGTH = 9;
const BEAM_QUAD_OVERSIZE = 2.2;
const BEAM_CORE_EXP = Math.LN2 * BEAM_QUAD_OVERSIZE * BEAM_QUAD_OVERSIZE;
const BEAM_HALO_EXP = BEAM_CORE_EXP * 0.22;
const PEAK_LUMINANCE = 0.92;
const BAND_PEAK_PER_LINE = 0.34;
const SPECTRAL_GAIN = 0.16;
const LOW_GAIN_MIN = 0.62;
const LOW_GAIN_MAX = 1;
const HIGH_GAIN_MIN = 0.25;
const HIGH_GAIN_MAX = 1;
const DYNAMICS_SWEEP_MIN = 0.6;
const DYNAMICS_SWEEP_MAX = 1.3;
const TAU_BANDS = 0.45;
const PANEL_COLOR = 0x1c2028;
const PRISM_GLASS = 0x1a2432;
const FALLBACK_EDGE = 0x9fc7e8;
const FALLBACK_HAZE = 0x090c14;
const EDGE_MAX_LUMA = 0.3;
const HAZE_BASE = 0x070910;
const HAZE_TINT = 0.08;
const HAZE_MAX_LUMA = 0.013;
const COLOR_LERP_MS = 900;
const TAU = Math.PI * 2;
function urlParam(name) {
  if (typeof location === "undefined") return null;
  return new URLSearchParams(location.search).get(name);
}
function approach(current, target, dt, tau) {
  return current + (target - current) * (1 - Math.exp(-dt / tau));
}
function refractiveIndex(lambdaNm) {
  const um = lambdaNm / 1e3;
  return CAUCHY_A + CAUCHY_B / (um * um);
}
function refract(dx, dy, nx, ny, eta) {
  const cosI = -(nx * dx + ny * dy);
  const sin2T = eta * eta * (1 - cosI * cosI);
  if (sin2T > 1) return null;
  const k = eta * cosI - Math.sqrt(1 - sin2T);
  return [eta * dx + k * nx, eta * dy + k * ny];
}
function raySegment(ox, oy, dx, dy, ax, ay, bx, by) {
  const ex = bx - ax, ey = by - ay;
  const denom = dx * ey - dy * ex;
  if (Math.abs(denom) < 1e-9) return -1;
  const t = ((ax - ox) * ey - (ay - oy) * ex) / denom;
  const u = ((ax - ox) * dy - (ay - oy) * dx) / denom;
  if (t <= 1e-6 || u < -1e-4 || u > 1 + 1e-4) return -1;
  return t;
}
const SPECTRUM_GAMMA = 0.8;
const RED_ROLLOFF_START = 600;
const RED_ROLLOFF_FLOOR = 0.12;
function wavelengthToRGB(l, out) {
  let r = 0, g = 0, b = 0;
  if (l < 440) {
    r = -(l - 440) / 60;
    g = 0;
    b = 1;
  } else if (l < 490) {
    r = 0;
    g = (l - 440) / 50;
    b = 1;
  } else if (l < 510) {
    r = 0;
    g = 1;
    b = -(l - 510) / 20;
  } else if (l < 580) {
    r = (l - 510) / 70;
    g = 1;
    b = 0;
  } else if (l < 645) {
    r = 1;
    g = -(l - 645) / 65;
    b = 0;
  } else {
    r = 1;
    g = 0;
    b = 0;
  }
  let f = 1;
  if (l < 420) f = 0.3 + 0.7 * (l - 380) / 40;
  else if (l > RED_ROLLOFF_START) {
    f = Math.max(
      RED_ROLLOFF_FLOOR,
      1 - (1 - RED_ROLLOFF_FLOOR) * (l - RED_ROLLOFF_START) / (700 - RED_ROLLOFF_START)
    );
  }
  out[0] = Math.pow(Math.max(0, r) * f, SPECTRUM_GAMMA);
  out[1] = Math.pow(Math.max(0, g) * f, SPECTRUM_GAMMA);
  out[2] = Math.pow(Math.max(0, b) * f, SPECTRUM_GAMMA);
  return out;
}
const beamVertexShader = `
precision mediump float;

attribute vec2 aOrigin;
attribute vec2 aDir;
attribute float aLen;
attribute vec2 aWidthPx;   // x at the source, y at the far end
attribute vec3 aColor;
attribute float aGain;

uniform float uPixelScale;

varying float vSide;
varying vec3 vColor;
varying float vGain;
varying float vSafeAtt;

${SAFE_ZONE_VERTEX_CHUNK}

void main() {
  vec2 along = aOrigin + aDir * (position.y * aLen);
  // The whole composition is coplanar and seen face-on, so the billboard
  // side vector is just the perpendicular in the plane - no cross product,
  // no degenerate case when looking down the axis.
  vec2 side = vec2(-aDir.y, aDir.x);

  vec4 mv = modelViewMatrix * vec4(along, 0.0, 1.0);
  // Width held in SCREEN pixels rather than world units, so a beam is the
  // same thin line whatever the camera does - laser-cathedral.js's own
  // reasoning, and the reason the widths in this file are quoted in px.
  float worldPerPx = max(-mv.z, 0.001) / uPixelScale;
  // Tapered along the beam. This is what makes the fan read as one white
  // shaft that resolves into colours rather than as sixteen coloured lines
  // from the outset: wide at the exit face, every beam overlaps its
  // neighbours and the additive sum is white, and they only draw apart as
  // the width falls away with distance.
  // QUAD_OVERSIZE: the quad has to be wider than the beam, because the
  // beam's own gaussian core occupies only the middle of it and the halo
  // needs somewhere to fall off. Sized so that aWidthPx is the core's
  // full width at half maximum - i.e. the width you actually see. Without
  // this the profile's exponent silently decided the width instead, and a
  // "14px" beam rendered as a 3.5px line: the beams then separated within a
  // tenth of the throw instead of staying merged over the first third.
  float widthPx = mix(aWidthPx.x, aWidthPx.y, position.y) * ${BEAM_QUAD_OVERSIZE.toFixed(2)};
  vec2 pos = along + side * (widthPx * 0.5 * worldPerPx * position.x);

  vSide = position.x;
  vColor = aColor;
  vGain = aGain;
  vec4 clip = projectionMatrix * modelViewMatrix * vec4(pos, 0.0, 1.0);
  gl_Position = clip;
  vSafeAtt = safeZoneAttenuation(clip);
}
`;
function beamFragmentShader(withHalo) {
  return `
precision mediump float;
varying float vSide;
varying vec3 vColor;
varying float vGain;
varying float vSafeAtt;
void main() {
  float s2 = vSide * vSide;
  // A tight core and, above tier 0, a wide soft halo. The halo is what makes
  // a beam read as light travelling through a medium instead of as a drawn
  // line - without it there is nothing for the fog to be.
  // Exponent chosen so the core's half-maximum falls exactly at the
  // requested width - see BEAM_QUAD_OVERSIZE in the vertex shader.
  float profile = exp(-s2 * ${BEAM_CORE_EXP.toFixed(3)})${withHalo ? ` + exp(-s2 * ${BEAM_HALO_EXP.toFixed(3)}) * 0.28` : ""};
  gl_FragColor = vec4(vColor * (profile * vGain * vSafeAtt), 1.0);
}
`;
}
const prismVertexShader = `
precision mediump float;
attribute vec3 aBary;
varying vec3 vBary;
varying vec2 vWorld;
void main() {
  vBary = aBary;
  vWorld = position.xy;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;
const prismFragmentShader = `
precision mediump float;
uniform vec3 uEdgeColor;
uniform vec3 uInteriorColor;
uniform float uEdgeGain;
uniform vec2 uEntry;
varying vec3 vBary;
varying vec2 vWorld;

void main() {
  float e = min(min(vBary.x, vBary.y), vBary.z);

  // The glass. A triangle drawn as an outline reads as a diagram; what makes
  // it read as a solid is that the interior is not flat. Three things vary
  // across it, all very dark:
  //  - thickness: the light path through a prism is longest at the base and
  //    goes to nothing at each edge, so the body is densest away from the
  //    edges. e is exactly that distance, so it stands in for depth.
  //  - a broad internal sheen sliding across the body, which is what a real
  //    piece of glass does with whatever is around it.
  //  - a faint bloom around where the beam actually enters, so the entry
  //    face is lit by its own light rather than uniformly.
  float body = smoothstep(0.0, 0.34, e);
  float sheen = 0.5 + 0.5 * sin((vWorld.x - vWorld.y) * 2.1);
  float entry = exp(-dot(vWorld - uEntry, vWorld - uEntry) * 1.9);
  vec3 col = uInteriorColor * (0.55 + 1.45 * body) * (0.75 + 0.5 * sheen);
  col += uEdgeColor * (entry * 0.045);

  // Thinner than before: the arris is a highlight on a solid, not the
  // subject. A hot core plus a short falloff.
  float edge = exp(-e * e * 11000.0) + exp(-e * e * 700.0) * 0.16;
  col += uEdgeColor * (edge * uEdgeGain);
  gl_FragColor = vec4(col, 1.0);
}
`;
function screenFragmentShader(count) {
  return `
precision mediump float;
uniform vec3 uHitColor[${count}];
uniform float uHitY[${count}];
uniform float uHitWidth[${count}];
uniform float uBandGain;
uniform vec3 uPanelColor;
varying vec2 vLocal;   // x across the panel (-1..1), y in world units

float hash1(vec2 p) {
  vec3 p3 = fract(vec3(p.xyx) * 0.1031);
  p3 += dot(p3, p3.yzx + 33.33);
  return fract((p3.x + p3.y) * p3.z);
}

void main() {
  // A MATTE surface: dark, but not flat. A fine tooth plus a very slow
  // large-scale unevenness, so it catches the eye as a wall rather than as
  // a black rectangle. Neutral, never palette-tinted - it is the surface the
  // spectrum is measured against, and a coloured screen tints the spectrum
  // on it (acceptance criterion 5).
  float tooth = hash1(floor(vLocal * vec2(150.0, 150.0)));
  float broad = sin(vLocal.y * 1.3 + 0.7) * sin(vLocal.x * 2.1);
  vec3 col = uPanelColor * (0.85 + 0.30 * tooth + 0.18 * broad);
  // Falls off toward the panel's edges, so it reads as lit from the beams'
  // side rather than as a uniformly printed slab.
  col *= 1.0 - abs(vLocal.x) * 0.42;

  // The light LANDS on the leading edge of the wall and spills a little way
  // across it - it does not pass through. Without this the band fills the
  // whole panel and the surface stops reading as a surface at all: it
  // becomes a printed rectangle of spectrum.
  float land = exp(-(vLocal.x + 1.0) * (vLocal.x + 1.0) * 18.0);
  for (int i = 0; i < ${count}; i++) {
    float d = (vLocal.y - uHitY[i]) / uHitWidth[i];
    col += uHitColor[i] * (exp(-d * d * 3.0) * uBandGain * land);
  }

  // Same dither as the background - the panel is a large near-flat dark
  // area, which is exactly where 8-bit banding shows.
  col += (fract(sin(dot(gl_FragCoord.xy, vec2(12.9898, 78.233))) * 43758.5453) - 0.5) / 255.0;
  gl_FragColor = vec4(max(col, vec3(0.0)), 1.0);
}
`;
}
const screenVertexShader = `
precision mediump float;
varying vec2 vLocal;
void main() {
  vLocal = vec2(position.x / ${(SCREEN_WIDTH * 0.5).toFixed(4)}, position.y + ${SCREEN_Y.toFixed(3)});
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;
const dustVertexShader = `
precision mediump float;
attribute float aSeed;
attribute float aSize;

uniform float uTime;
uniform vec2 uFanOrigin;
uniform vec2 uFanDir;
uniform float uFanSpread;
uniform float uGain;
uniform float uPointScale;

varying float vBright;

${SAFE_ZONE_VERTEX_CHUNK}

void main() {
  // Slow drift, wrapped in the box the beams travel through.
  vec3 p = position;
  p.x = mod(p.x + uTime * (0.06 + aSeed * 0.05) + 8.0, 16.0) - 8.0;
  p.y += sin(uTime * (0.10 + aSeed * 0.08) + aSeed * 6.28) * 0.22;

  // Distance from the mote to the fan: perpendicular distance to the central
  // ray, tapered by how far along it the mote sits, which is what makes the
  // lit volume a wedge rather than a tube.
  vec2 rel = p.xy - uFanOrigin;
  float along = dot(rel, uFanDir);
  vec2 perpV = rel - uFanDir * along;
  float perp = length(perpV);
  float halfWidth = 0.10 + max(along, 0.0) * uFanSpread;
  float inside = 1.0 - smoothstep(halfWidth * 0.4, halfWidth * 1.5, perp);
  inside *= step(0.0, along);

  vec4 clip = projectionMatrix * modelViewMatrix * vec4(p, 1.0);
  gl_Position = clip;
  gl_PointSize = aSize * uPointScale;
  vBright = (0.05 + 0.95 * inside) * uGain * safeZoneAttenuation(clip);
}
`;
const dustFragmentShader = `
precision mediump float;
uniform vec3 uColor;
varying float vBright;
void main() {
  vec2 d = gl_PointCoord - vec2(0.5);
  float r2 = dot(d, d) * 4.0;
  if (r2 > 1.0) discard;
  gl_FragColor = vec4(uColor * ((1.0 - smoothstep(0.1, 1.0, r2)) * vBright), 1.0);
}
`;
const hazeVertexShader = `
precision mediump float;
varying vec2 vUv;
void main() {
  vUv = uv;
  gl_Position = vec4(position.xy * 2.0, 0.0, 1.0);
}
`;
const hazeFragmentShader = `
precision mediump float;
uniform vec3 uHaze;
uniform float uAspect;
varying vec2 vUv;
void main() {
  // Brightest around the prism's side of the frame, falling away - a room
  // with one light source in it, not a flat field. Deep black either way:
  // a colour is judged relative to what surrounds it, and a tinted ground
  // makes the spectrum on it read wrong. See HAZE_TINT.
  vec2 p = (vUv - vec2(0.34, 0.52)) * vec2(uAspect, 1.0);
  float d = dot(p, p);
  vec3 col = uHaze * (0.22 + 0.78 * exp(-d * 1.15));

  // ORDERED DITHER, immediately before the 8-bit quantisation - the same fix
  // constellation.js uses and for the same reason. A field this dark holds
  // each output level across tens of pixels, and a plateau that wide IS a
  // visible ring. Half a level of noise at exactly the quantisation step
  // scatters the boundary instead of masking it.
  col += (fract(sin(dot(gl_FragCoord.xy, vec2(12.9898, 78.233))) * 43758.5453) - 0.5) / 255.0;
  gl_FragColor = vec4(max(col, vec3(0.0)), 1.0);
}
`;
function capLuma(color, maxLuma) {
  const l = 0.2126 * color.r + 0.7152 * color.g + 0.0722 * color.b;
  if (l > maxLuma && l > 0) color.multiplyScalar(maxLuma / l);
  return color;
}
export class PrismVisualizer extends Visualizer {
  constructor(options) {
    super();
    const tier = options && options.qualityTier != null ? options.qualityTier : 1;
    this._tier = TIER_WAVELENGTHS[tier] != null ? tier : 1;
    this._count = TIER_WAVELENGTHS[this._tier];
    this._debugGain = parseFloat(urlParam("prismgain"));
  }
  init(scene, camera) {
    this._scene = scene;
    this._camera = camera;
    camera.position.set(0, 0, CAMERA_Z);
    camera.lookAt(0, 0, 0);
    const n = this._count;
    this._lambda = new Float64Array(n);
    this._baseColor = new Float32Array(n * 3);
    const rgb = [0, 0, 0];
    let sr = 0, sg = 0, sb = 0;
    for (let i = 0; i < n; i++) {
      const l = LAMBDA_MIN + (LAMBDA_MAX - LAMBDA_MIN) * (n === 1 ? 0.5 : i / (n - 1));
      this._lambda[i] = l;
      wavelengthToRGB(l, rgb);
      this._baseColor[i * 3] = rgb[0];
      this._baseColor[i * 3 + 1] = rgb[1];
      this._baseColor[i * 3 + 2] = rgb[2];
      sr += rgb[0];
      sg += rgb[1];
      sb += rgb[2];
    }
    const wb = [sg / Math.max(sr, 1e-6), 1, sg / Math.max(sb, 1e-6)];
    for (let i = 0; i < n; i++) {
      this._baseColor[i * 3] *= wb[0];
      this._baseColor[i * 3 + 2] *= wb[2];
    }
    this._index = new Float64Array(n);
    for (let i = 0; i < n; i++) this._index[i] = refractiveIndex(this._lambda[i]);
    this._buildHaze(scene);
    this._buildPrism(scene);
    this._buildBeams(scene);
    this._buildScreen(scene);
    this._buildDust(scene);
    this._time = 0;
    this._lowGain = LOW_GAIN_MIN;
    this._highGain = HIGH_GAIN_MIN;
    this._sweep = 0;
    this._prismX = PRISM_X_FAR;
    this._lastPaletteRef = null;
    this._colorLerp = 1;
    this._edgeFrom = new THREE.Color(FALLBACK_EDGE);
    this._edgeTo = new THREE.Color(FALLBACK_EDGE);
    this._hazeFrom = new THREE.Color(FALLBACK_HAZE);
    this._scratch = new THREE.Color();
    this._base = new THREE.Color();
    this._hazeTo = new THREE.Color(FALLBACK_HAZE);
    this._applyPalette(FALLBACK_EDGE, FALLBACK_HAZE);
    this._edgeFrom.copy(this._edgeTo);
    this._hazeFrom.copy(this._hazeTo);
    this._writeColors(1);
    this._solve(0.5);
  }
  _buildHaze(scene) {
    this._hazeUniforms = { uHaze: { value: new THREE.Color(FALLBACK_HAZE) }, uAspect: safeZoneUniforms.uAspect };
    this._hazeGeo = new THREE.PlaneGeometry(1, 1);
    this._hazeMat = new THREE.ShaderMaterial({
      uniforms: this._hazeUniforms,
      vertexShader: hazeVertexShader,
      fragmentShader: hazeFragmentShader,
      depthTest: false,
      depthWrite: false
    });
    this._hazeMesh = new THREE.Mesh(this._hazeGeo, this._hazeMat);
    this._hazeMesh.frustumCulled = false;
    this._hazeMesh.renderOrder = -1e3;
    scene.add(this._hazeMesh);
  }
  _buildPrism(scene) {
    this._prismGeo = new THREE.BufferGeometry();
    this._prismPos = new Float32Array(9);
    this._prismGeo.setAttribute("position", new THREE.BufferAttribute(this._prismPos, 3));
    this._prismGeo.setAttribute("aBary", new THREE.BufferAttribute(
      new Float32Array([1, 0, 0, 0, 1, 0, 0, 0, 1]),
      3
    ));
    this._prismGeo.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e6);
    this._prismUniforms = {
      uEdgeColor: { value: new THREE.Color(FALLBACK_EDGE) },
      uInteriorColor: { value: new THREE.Color(PRISM_GLASS) },
      uEdgeGain: { value: 1 },
      uEntry: { value: new THREE.Vector2() }
    };
    this._prismMat = new THREE.ShaderMaterial({
      uniforms: this._prismUniforms,
      vertexShader: prismVertexShader,
      fragmentShader: prismFragmentShader,
      transparent: true,
      blending: THREE.AdditiveBlending,
      side: THREE.DoubleSide,
      depthTest: false,
      depthWrite: false
    });
    this._prismMesh = new THREE.Mesh(this._prismGeo, this._prismMat);
    this._prismMesh.frustumCulled = false;
    this._prismMesh.renderOrder = -10;
    scene.add(this._prismMesh);
  }
  _buildBeams(scene) {
    const quad = new THREE.InstancedBufferGeometry();
    quad.setAttribute("position", new THREE.BufferAttribute(new Float32Array([
      -1,
      0,
      0,
      1,
      0,
      0,
      1,
      1,
      0,
      -1,
      1,
      0
    ]), 3));
    quad.setIndex([0, 1, 2, 0, 2, 3]);
    const cap = MAX_BEAMS;
    this._bOrigin = new Float32Array(cap * 2);
    this._bDir = new Float32Array(cap * 2);
    this._bLen = new Float32Array(cap);
    this._bWidth = new Float32Array(cap * 2);
    this._bColor = new Float32Array(cap * 3);
    this._bGain = new Float32Array(cap);
    quad.setAttribute("aOrigin", new THREE.InstancedBufferAttribute(this._bOrigin, 2));
    quad.setAttribute("aDir", new THREE.InstancedBufferAttribute(this._bDir, 2));
    quad.setAttribute("aLen", new THREE.InstancedBufferAttribute(this._bLen, 1));
    quad.setAttribute("aWidthPx", new THREE.InstancedBufferAttribute(this._bWidth, 2));
    quad.setAttribute("aColor", new THREE.InstancedBufferAttribute(this._bColor, 3));
    quad.setAttribute("aGain", new THREE.InstancedBufferAttribute(this._bGain, 1));
    quad.instanceCount = this._count + 2;
    quad.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e6);
    this._beamGeo = quad;
    this._beamUniforms = Object.assign({}, safeZoneUniforms, { uPixelScale: { value: 1 } });
    this._beamMat = new THREE.ShaderMaterial({
      uniforms: this._beamUniforms,
      vertexShader: beamVertexShader,
      fragmentShader: beamFragmentShader(TIER_HALO[this._tier]),
      transparent: true,
      blending: THREE.AdditiveBlending,
      // The quad's winding depends on which way the beam points: `side` is the
      // direction rotated +90, so (side, dir) is left-handed on screen and
      // every triangle comes out back-facing. With the default THREE.FrontSide the
      // beams compile, upload correct data, and rasterise nothing at all.
      side: THREE.DoubleSide,
      depthTest: false,
      depthWrite: false
    });
    this._beamMesh = new THREE.Mesh(quad, this._beamMat);
    this._beamMesh.frustumCulled = false;
    this._beamMesh.renderOrder = 10;
    scene.add(this._beamMesh);
  }
  _buildScreen(scene) {
    const n = this._count;
    this._screenUniforms = {
      uHitColor: { value: Array.from({ length: n }, () => new THREE.Color(0, 0, 0)) },
      uHitY: { value: new Array(n).fill(0) },
      uHitWidth: { value: new Array(n).fill(0.05) },
      uBandGain: { value: 1 },
      uPanelColor: { value: new THREE.Color(PANEL_COLOR) }
    };
    this._screenGeo = new THREE.PlaneGeometry(SCREEN_WIDTH, SCREEN_HALF_HEIGHT * 2);
    this._screenMat = new THREE.ShaderMaterial({
      uniforms: this._screenUniforms,
      vertexShader: screenVertexShader,
      fragmentShader: screenFragmentShader(n),
      // OPAQUE, and not additive. A projection screen is a surface, and this
      // one has to hide the haze behind it: drawn additively over a
      // palette-tinted haze, the band measured up to 88/255 different between
      // a blue cover and a red one - which is acceptance criterion 5 failing,
      // even though the light being added was identical. The panel occludes
      // the haze and is itself neutral, so what lands on it is the spectrum
      // and nothing else.
      transparent: false,
      blending: THREE.NormalBlending,
      side: THREE.DoubleSide,
      depthTest: false,
      depthWrite: false
    });
    this._screenMesh = new THREE.Mesh(this._screenGeo, this._screenMat);
    this._screenMesh.position.set(SCREEN_FRONT_X + SCREEN_WIDTH * 0.5, SCREEN_Y, -0.01);
    this._screenMesh.frustumCulled = false;
    this._screenMesh.renderOrder = 5;
    scene.add(this._screenMesh);
  }
  _buildDust(scene) {
    const count = TIER_PARTICLES[this._tier];
    this._dust = null;
    if (count <= 0) return;
    const pos = new Float32Array(count * 3);
    const seed = new Float32Array(count);
    const size = new Float32Array(count);
    for (let i = 0; i < count; i++) {
      pos[i * 3] = -8 + Math.random() * 16;
      pos[i * 3 + 1] = -4.2 + Math.random() * 8.4;
      pos[i * 3 + 2] = -0.3 + Math.random() * 0.6;
      seed[i] = Math.random();
      size[i] = 1.3 + Math.random() * 2.2;
    }
    this._dustGeo = new THREE.BufferGeometry();
    this._dustGeo.setAttribute("position", new THREE.BufferAttribute(pos, 3));
    this._dustGeo.setAttribute("aSeed", new THREE.BufferAttribute(seed, 1));
    this._dustGeo.setAttribute("aSize", new THREE.BufferAttribute(size, 1));
    this._dustGeo.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e6);
    this._dustUniforms = Object.assign({}, safeZoneUniforms, {
      uTime: { value: 0 },
      uFanOrigin: { value: new THREE.Vector2() },
      uFanDir: { value: new THREE.Vector2(1, 0) },
      uFanSpread: { value: 0.08 },
      uGain: { value: HIGH_GAIN_MIN },
      uPointScale: { value: 1 },
      uColor: { value: new THREE.Color(14674175) }
    });
    this._dustMat = new THREE.ShaderMaterial({
      uniforms: this._dustUniforms,
      vertexShader: dustVertexShader,
      fragmentShader: dustFragmentShader,
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthTest: false,
      depthWrite: false
    });
    this._dust = new THREE.Points(this._dustGeo, this._dustMat);
    this._dust.frustumCulled = false;
    this._dust.renderOrder = 8;
    scene.add(this._dust);
  }
  // One ray, at the middle wavelength, for the aiming loop above: the same
  // trace the full fan uses, reduced to what aiming needs - where the ray
  // lands on the screen, and how long the throw is. Returns null when the
  // prism cannot transmit at this orientation, in which case the aim simply
  // holds its previous value.
  _traceMid(alpha, theta, cx, cy) {
    const beta = alpha - Math.PI + theta;
    const phi = beta - 150 * Math.PI / 180;
    const vx = this._avx || (this._avx = new Float64Array(3));
    const vy = this._avy || (this._avy = new Float64Array(3));
    for (let k = 0; k < 3; k++) {
      const a2 = phi + Math.PI / 2 + k * (TAU / 3);
      vx[k] = cx + PRISM_RADIUS * Math.cos(a2);
      vy[k] = cy + PRISM_RADIUS * Math.sin(a2);
    }
    const ex = (vx[0] + vx[1]) * 0.5, ey = (vy[0] + vy[1]) * 0.5;
    const dx = Math.cos(alpha), dy = Math.sin(alpha);
    const nn = this._index[this._count >> 1];
    const inside = refract(dx, dy, Math.cos(beta), Math.sin(beta), 1 / nn);
    if (!inside) return null;
    let best = -1, bestFace = -1;
    for (let f = 1; f <= 2; f++) {
      const a2 = f, b2 = (f + 1) % 3;
      const t = raySegment(ex, ey, inside[0], inside[1], vx[a2], vy[a2], vx[b2], vy[b2]);
      if (t > 0 && (best < 0 || t < best)) {
        best = t;
        bestFace = a2;
      }
    }
    if (best < 0) return null;
    const hx = ex + inside[0] * best, hy = ey + inside[1] * best;
    const a = bestFace, b = (bestFace + 1) % 3;
    let fnx = vy[b] - vy[a], fny = -(vx[b] - vx[a]);
    const fl = Math.hypot(fnx, fny) || 1;
    fnx /= fl;
    fny /= fl;
    const mx = (vx[a] + vx[b]) * 0.5 - cx, my = (vy[a] + vy[b]) * 0.5 - cy;
    if (fnx * mx + fny * my < 0) {
      fnx = -fnx;
      fny = -fny;
    }
    const out = refract(inside[0], inside[1], -fnx, -fny, nn);
    if (!out || out[0] < 0.05) return null;
    const run = SCREEN_FRONT_X - hx;
    const len = run / out[0];
    if (!(len > 0.2)) return null;
    return { y: hy + out[1] * len, throw: run };
  }
  // --- the trace -------------------------------------------------------------
  // Everything geometric in the frame comes out of this one method, run once
  // per frame: the triangle's vertices, where the beam enters, the path inside
  // the glass, where each wavelength leaves and at what angle, and where each
  // one lands on the screen. No part of the fan is interpolated or drawn to
  // taste - see the file header.
  _solve(progress) {
    const n = this._count;
    const theta = THETA_BASE + THETA_LFO * Math.sin(this._time * TAU / THETA_LFO_PERIOD);
    const arch = Math.sin(Math.PI * progress);
    this._prismX = PRISM_X_NEAR + (PRISM_X_FAR - PRISM_X_NEAR) * arch;
    const cx = this._prismX, cy = PRISM_Y;
    let aim = this._aim != null ? this._aim : ALPHA_START;
    for (let k = 0; k < AIM_ITERATIONS; k++) {
      const probe = this._traceMid(aim, theta, cx, cy);
      if (!probe) break;
      const err = BAND_CENTER_Y - probe.y;
      if (Math.abs(err) < 2e-3) break;
      aim += err / Math.max(probe.throw, 0.5);
    }
    this._aim = aim;
    const alpha = aim + this._sweep + ALPHA_SWEEP * 0.35 * Math.sin(this._time * TAU / ALPHA_LFO_PERIOD);
    const beta = alpha - Math.PI + theta;
    const phi = beta - 150 * Math.PI / 180;
    this._phi = phi;
    const vx = this._vx || (this._vx = new Float64Array(3));
    const vy = this._vy || (this._vy = new Float64Array(3));
    for (let k = 0; k < 3; k++) {
      const a = phi + Math.PI / 2 + k * (TAU / 3);
      vx[k] = cx + PRISM_RADIUS * Math.cos(a);
      vy[k] = cy + PRISM_RADIUS * Math.sin(a);
    }
    const p = this._prismPos;
    for (let k = 0; k < 3; k++) {
      p[k * 3] = vx[k];
      p[k * 3 + 1] = vy[k];
      p[k * 3 + 2] = 0;
    }
    this._prismGeo.attributes.position.needsUpdate = true;
    const n1x = Math.cos(beta), n1y = Math.sin(beta);
    const ex = (vx[0] + vx[1]) * 0.5, ey = (vy[0] + vy[1]) * 0.5;
    const dx = Math.cos(alpha), dy = Math.sin(alpha);
    const exitX = this._exitX || (this._exitX = new Float64Array(n));
    const exitY = this._exitY || (this._exitY = new Float64Array(n));
    const outA = this._outA || (this._outA = new Float64Array(n));
    const ok = this._ok || (this._ok = new Uint8Array(n));
    let meanA = 0, meanCount = 0;
    let midX = ex, midY = ey;
    for (let i = 0; i < n; i++) {
      ok[i] = 0;
      const nn = this._index[i];
      const inside = refract(dx, dy, n1x, n1y, 1 / nn);
      if (!inside) continue;
      const ix = inside[0], iy = inside[1];
      let best = -1, bestFace = -1;
      for (let f = 1; f <= 2; f++) {
        const a2 = f, b2 = (f + 1) % 3;
        const t = raySegment(ex, ey, ix, iy, vx[a2], vy[a2], vx[b2], vy[b2]);
        if (t > 0 && (best < 0 || t < best)) {
          best = t;
          bestFace = a2;
        }
      }
      if (best < 0) continue;
      const hx = ex + ix * best, hy = ey + iy * best;
      const a = bestFace, b = (bestFace + 1) % 3;
      let fnx = vy[b] - vy[a], fny = -(vx[b] - vx[a]);
      const fl = Math.hypot(fnx, fny) || 1;
      fnx /= fl;
      fny /= fl;
      const mx = (vx[a] + vx[b]) * 0.5 - cx, my = (vy[a] + vy[b]) * 0.5 - cy;
      if (fnx * mx + fny * my < 0) {
        fnx = -fnx;
        fny = -fny;
      }
      const out = refract(ix, iy, -fnx, -fny, nn);
      if (!out) continue;
      exitX[i] = hx;
      exitY[i] = hy;
      outA[i] = Math.atan2(out[1], out[0]);
      ok[i] = 1;
      meanA += outA[i];
      meanCount++;
      if (i === n >> 1) {
        midX = hx;
        midY = hy;
      }
    }
    if (meanCount === 0) {
      this._traced = false;
      return;
    }
    meanA /= meanCount;
    for (let i = 0; i < n; i++) if (ok[i]) outA[i] = meanA + (outA[i] - meanA) * DISPERSION_GAIN;
    const dens = this._dens || (this._dens = new Float64Array(n));
    let meanGap = 0, gaps = 0;
    for (let i = 0; i < n; i++) {
      const a0 = outA[Math.max(0, i - 1)], a1 = outA[Math.min(n - 1, i + 1)];
      const span = Math.max(1, Math.min(n - 1, i + 1) - Math.max(0, i - 1));
      dens[i] = Math.abs(a1 - a0) / span;
      if (ok[i]) {
        meanGap += dens[i];
        gaps++;
      }
    }
    meanGap = gaps > 0 ? meanGap / gaps : 1;
    for (let i = 0; i < n; i++) dens[i] = meanGap > 1e-9 ? dens[i] / meanGap : 1;
    this._traced = true;
    this._entryX = ex;
    this._entryY = ey;
    this._prismUniforms.uEntry.value.set(ex, ey);
    this._midX = midX;
    this._midY = midY;
    this._incidentDX = dx;
    this._incidentDY = dy;
    this._meanAngle = meanA;
    return { n, exitX, exitY, outA, ok };
  }
  // Writes the traced result into the instanced beam attributes and the
  // screen's uniforms. The band on the screen is the same set of gaussians the
  // beams are, evaluated at the analytically computed landing points, so the
  // two can never disagree.
  _writeBeams(worldPerPx) {
    const n = this._count;
    const O = this._bOrigin, D2 = this._bDir, L = this._bLen, W2 = this._bWidth, C2 = this._bColor, G6 = this._bGain;
    const ix = this._entryX - this._incidentDX * INCIDENT_LENGTH;
    const iy = this._entryY - this._incidentDY * INCIDENT_LENGTH;
    O[0] = ix;
    O[1] = iy;
    D2[0] = this._incidentDX;
    D2[1] = this._incidentDY;
    L[0] = INCIDENT_LENGTH;
    W2[0] = W2[1] = INCIDENT_WIDTH_PX;
    C2[0] = C2[1] = C2[2] = PEAK_LUMINANCE;
    G6[0] = this._lowGain;
    const sx = this._midX - this._entryX, sy = this._midY - this._entryY;
    const sl = Math.hypot(sx, sy) || 1;
    O[2] = this._entryX;
    O[3] = this._entryY;
    D2[2] = sx / sl;
    D2[3] = sy / sl;
    L[1] = sl;
    W2[2] = W2[3] = INCIDENT_WIDTH_PX * 0.8;
    C2[3] = C2[4] = C2[5] = PEAK_LUMINANCE * 0.72;
    G6[1] = this._lowGain * 0.85;
    let sr = 0, sg = 0, sb = 0;
    for (let i = 0; i < n; i++) {
      if (!this._ok[i]) continue;
      const d = this._dens[i];
      sr += this._baseColor[i * 3] * d;
      sg += this._baseColor[i * 3 + 1] * d;
      sb += this._baseColor[i * 3 + 2] * d;
    }
    const wbR = sr > 1e-5 ? sg / sr : 1;
    const wbB = sb > 1e-5 ? sg / sb : 1;
    const hitY = this._screenUniforms.uHitY.value;
    const hitW = this._screenUniforms.uHitWidth.value;
    const hitC = this._screenUniforms.uHitColor.value;
    let spreadTan = 0.02;
    for (let i = 0; i < n; i++) {
      const s = i + 2;
      const on = this._ok[i];
      const ax = Math.cos(this._outA[i]), ay = Math.sin(this._outA[i]);
      O[s * 2] = this._exitX[i];
      O[s * 2 + 1] = this._exitY[i];
      D2[s * 2] = ax;
      D2[s * 2 + 1] = ay;
      let len = 6;
      if (on && ax > 0.05) {
        len = (SCREEN_FRONT_X - this._exitX[i]) / ax;
        if (!(len > 0.2)) len = 0.2;
      }
      L[s] = len;
      W2[s * 2] = SPECTRAL_WIDTH_NEAR_PX;
      W2[s * 2 + 1] = SPECTRAL_WIDTH_FAR_PX;
      C2[s * 3] = this._baseColor[i * 3] * wbR;
      C2[s * 3 + 1] = this._baseColor[i * 3 + 1];
      C2[s * 3 + 2] = this._baseColor[i * 3 + 2] * wbB;
      G6[s] = on ? this._lowGain * SPECTRAL_GAIN * this._dens[i] : 0;
      const y = this._exitY[i] + ay * len;
      hitY[i] = y;
      hitW[i] = Math.max(0.02, worldPerPx * SPECTRAL_WIDTH_FAR_PX * 1.15);
      const dw = BAND_PEAK_PER_LINE * this._dens[i];
      hitC[i].setRGB(
        this._baseColor[i * 3] * dw * wbR,
        this._baseColor[i * 3 + 1] * dw,
        this._baseColor[i * 3 + 2] * dw * wbB
      );
      if (on) spreadTan = Math.max(spreadTan, Math.abs(Math.tan(this._outA[i] - this._meanAngle)));
    }
    this._beamGeo.attributes.aOrigin.needsUpdate = true;
    this._beamGeo.attributes.aDir.needsUpdate = true;
    this._beamGeo.attributes.aLen.needsUpdate = true;
    this._beamGeo.attributes.aWidthPx.needsUpdate = true;
    this._beamGeo.attributes.aColor.needsUpdate = true;
    this._beamGeo.attributes.aGain.needsUpdate = true;
    this._screenUniforms.uBandGain.value = this._lowGain;
    if (this._dust) {
      this._dustUniforms.uFanOrigin.value.set(this._midX, this._midY);
      this._dustUniforms.uFanDir.value.set(Math.cos(this._meanAngle), Math.sin(this._meanAngle));
      this._dustUniforms.uFanSpread.value = spreadTan;
    }
  }
  // The cover acts here and nowhere else: the prism's edges, the haze and a
  // faint ambient wash. Not the spectrum - that is physical, and tinting it
  // would make it wrong. A stated constraint of the brief, not an omission.
  _applyPalette(dominant, vivid) {
    this._edgeTo.set(vivid);
    capLuma(this._edgeTo, EDGE_MAX_LUMA);
    const cover = this._scratch.set(dominant);
    const mx = Math.max(cover.r, Math.max(cover.g, cover.b));
    if (mx > 1e-4) cover.multiplyScalar(1 / mx);
    const base = this._base.set(HAZE_BASE);
    this._hazeTo.setRGB(
      base.r * (1 - HAZE_TINT) + cover.r * HAZE_TINT * base.r * 8,
      base.g * (1 - HAZE_TINT) + cover.g * HAZE_TINT * base.g * 8,
      base.b * (1 - HAZE_TINT) + cover.b * HAZE_TINT * base.b * 8
    );
    capLuma(this._hazeTo, HAZE_MAX_LUMA);
  }
  _writeColors(t) {
    this._prismUniforms.uEdgeColor.value.copy(this._edgeFrom).lerp(this._edgeTo, t);
    this._hazeUniforms.uHaze.value.copy(this._hazeFrom).lerp(this._hazeTo, t);
    if (this._dustUniforms) {
      this._dustUniforms.uColor.value.setRGB(1, 1, 1).lerp(this._prismUniforms.uEdgeColor.value, 0.35);
    }
  }
  update(deltaTime, profile) {
    const paused = profile.playbackState !== "PLAYING";
    if (!paused) this._time += deltaTime;
    const director = profile.director;
    const bands = director && director.bands;
    const character = director && director.character;
    const dynamics = character && character.dynamics != null ? character.dynamics : 0.5;
    const hasBands = !!(director && director.hasReliableGrid && bands);
    if (!paused) {
      const low = hasBands ? bands.low : 0.5 + 0.34 * Math.sin(this._time * TAU / 31);
      const mid = hasBands ? bands.mid : 0.5 + 0.42 * Math.sin(this._time * TAU / 23 + 1.1);
      const high = hasBands ? bands.high : 0.5 + 0.3 * Math.sin(this._time * TAU / 17 + 2.3);
      this._lowGain = approach(
        this._lowGain,
        LOW_GAIN_MIN + (LOW_GAIN_MAX - LOW_GAIN_MIN) * low,
        deltaTime,
        TAU_BANDS
      );
      this._highGain = approach(
        this._highGain,
        HIGH_GAIN_MIN + (HIGH_GAIN_MAX - HIGH_GAIN_MIN) * high,
        deltaTime,
        TAU_BANDS
      );
      const amp = DYNAMICS_SWEEP_MIN + (DYNAMICS_SWEEP_MAX - DYNAMICS_SWEEP_MIN) * dynamics;
      this._sweep = approach(this._sweep, (mid - 0.5) * 2 * ALPHA_SWEEP * amp, deltaTime, TAU_BANDS);
    }
    const progress = profile.duration > 0 ? THREE.MathUtils.clamp(profile.progress, 0, 1) : 0.5;
    this._solve(progress);
    const canvasPixelHeight = window.innerHeight * Math.min(window.devicePixelRatio || 1, 2);
    const pixelScale = canvasPixelHeight / (2 * Math.tan(THREE.MathUtils.degToRad(this._camera.fov) / 2));
    this._beamUniforms.uPixelScale.value = pixelScale;
    if (this._traced) this._writeBeams(CAMERA_Z / Math.max(pixelScale, 1e-3));
    this._prismUniforms.uEdgeGain.value = 0.55 + 0.45 * this._lowGain;
    if (this._dust) {
      this._dustUniforms.uTime.value = this._time;
      this._dustUniforms.uGain.value = this._highGain;
      this._dustUniforms.uPointScale.value = Math.max(0.6, canvasPixelHeight / 1080);
    }
    const palette = profile.artworkPalette;
    if (palette !== this._lastPaletteRef) {
      this._lastPaletteRef = palette;
      const dominant = palette && palette.dominantColors && palette.dominantColors.length ? palette.dominantColors[0] : FALLBACK_HAZE;
      const vivid = palette && palette.vividColors && palette.vividColors.length ? palette.vividColors[0] : FALLBACK_EDGE;
      this._edgeFrom.copy(this._prismUniforms.uEdgeColor.value);
      this._hazeFrom.copy(this._hazeUniforms.uHaze.value);
      this._applyPalette(dominant, vivid);
      this._colorLerp = 0;
    }
    if (this._colorLerp < 1) {
      this._colorLerp = Math.min(1, this._colorLerp + deltaTime * 1e3 / COLOR_LERP_MS);
      this._writeColors(this._colorLerp);
    }
  }
  dispose() {
    for (const m of [this._hazeMesh, this._prismMesh, this._beamMesh, this._screenMesh, this._dust]) {
      if (m) this._scene.remove(m);
    }
    this._hazeGeo.dispose();
    this._hazeMat.dispose();
    this._prismGeo.dispose();
    this._prismMat.dispose();
    this._beamGeo.dispose();
    this._beamMat.dispose();
    this._screenGeo.dispose();
    this._screenMat.dispose();
    if (this._dust) {
      this._dustGeo.dispose();
      this._dustMat.dispose();
      this._dust = null;
    }
    this._scene = null;
  }
}
